import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';

const SCROLL_THRESHOLD = 100;
const USER_MSG_TOP_PADDING = 16;
const CONTENT_BOTTOM_PADDING = 24;
const PROGRAMMATIC_IGNORE_MS = 150;
// CSS custom property on the scroll container holding the empty room kept under
// the last message (see updateTailSpace). Read by .chat-container__messages.
const TAIL_SPACE_VAR = '--chat-tail-space';

export function useAutoScroll(
  messages: { role: string }[],
  streamingContent?: string
) {
  const ref = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);
  const programmaticUntil = useRef(0);
  const streamingWasActive = useRef(false);

  // Separate length trackers for the two layout effects so they don't interfere
  const prevLenLayout = useRef(0); // for user-message layout effect
  const prevLenEffect = useRef(0); // for AI-message async effect

  const isNearBottom = useCallback(() => {
    const el = ref.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_THRESHOLD;
  }, []);

  // The user message the view was last pinned to, or null when nothing is pinned
  // (a history load, or a fresh conversation).
  const pinnedUserMsg = useRef<HTMLElement | null>(null);

  // ── Room under the last message ──────────────────────────────────────────
  // Pinning a new question to the top of the view needs enough scrollable
  // space below it, before the answer exists to fill it (Claude.ai's
  // behaviour). That room used to be a fixed 75vh of padding, left under every
  // conversation for good — so you could always scroll into a screenful of
  // nothing. Now it is exactly what the pin needs: the viewport less whatever
  // is already below the question, shrinking as the answer grows, zero once
  // the answer fills the view, and zero when nothing is pinned.
  const updateTailSpace = useCallback(() => {
    const container = ref.current;
    if (!container) return;

    let pinned = pinnedUserMsg.current;
    if (pinned && !pinned.isConnected) {
      const userMsgs = container.querySelectorAll('.chat-msg--user');
      pinned = (userMsgs[userMsgs.length - 1] as HTMLElement | undefined) ?? null;
      pinnedUserMsg.current = pinned;
    }

    let space = 0;
    const content = container.querySelector('.chat-container__messages');
    const last = content?.lastElementChild as HTMLElement | null | undefined;
    if (pinned && last) {
      const cRect = container.getBoundingClientRect();
      const toScroll = (y: number) => y - cRect.top + container.scrollTop;
      const below = toScroll(last.getBoundingClientRect().bottom) - toScroll(pinned.getBoundingClientRect().top);
      const padBottom = parseFloat(getComputedStyle(container).paddingBottom) || 0;
      space = Math.max(0, Math.ceil(container.clientHeight - below - USER_MSG_TOP_PADDING - padBottom));
    }

    const current = parseFloat(container.style.getPropertyValue(TAIL_SPACE_VAR)) || 0;
    if (Math.abs(current - space) > 1) container.style.setProperty(TAIL_SPACE_VAR, `${space}px`);
  }, []);

  const scrollToEl = useCallback((container: HTMLElement, el: HTMLElement) => {
    const cRect = container.getBoundingClientRect();
    const eRect = el.getBoundingClientRect();
    const needed = container.scrollTop + (eRect.bottom - cRect.bottom) + CONTENT_BOTTOM_PADDING;
    if (needed > container.scrollTop) {
      programmaticUntil.current = Date.now() + PROGRAMMATIC_IGNORE_MS;
      container.scrollTop = needed;
    }
  }, []);

  // ── Track user scroll (ignore programmatic scrolls) ──────────────────────
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handleScroll = () => {
      if (Date.now() < programmaticUntil.current) return;
      if (!isNearBottom()) {
        userScrolledUp.current = true;
      } else {
        userScrolledUp.current = false;
      }
    };
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        userScrolledUp.current = true;
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    el.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      el.removeEventListener('scroll', handleScroll);
      el.removeEventListener('wheel', handleWheel);
    };
  }, [isNearBottom]);

  // ── User message: position synchronously before paint ────────────────────
  // Pins the view to the new question and stays there — the response is
  // never auto-followed while streaming, so the user can read at their own
  // pace without the page yanking down to the bottom.
  useLayoutEffect(() => {
    const len = messages.length;
    const prev = prevLenLayout.current;
    prevLenLayout.current = len;

    if (len < prev || (len > 0 && prev === 0)) {
      // Conversation switched or (re)loaded from history: nothing is pinned.
      pinnedUserMsg.current = null;
    }
    if (len <= prev) return; // reset or unchanged
    const lastMsg = messages[len - 1];
    if (lastMsg?.role !== 'user') return;

    const container = ref.current;
    if (!container) return;

    userScrolledUp.current = false;

    const userMsgs = container.querySelectorAll('.chat-msg--user');
    const lastUserEl = userMsgs[userMsgs.length - 1] as HTMLElement | undefined;
    if (lastUserEl) {
      // Make the room first, synchronously, so the scroll below can reach it.
      pinnedUserMsg.current = lastUserEl;
      updateTailSpace();
      const cRect = container.getBoundingClientRect();
      const mRect = lastUserEl.getBoundingClientRect();
      programmaticUntil.current = Date.now() + PROGRAMMATIC_IGNORE_MS;
      container.scrollTop = Math.max(0, container.scrollTop + (mRect.top - cRect.top) - USER_MSG_TOP_PADDING);
    }
  }, [messages, updateTailSpace]);

  // Keep the room in step with the answer: re-measured on every render that
  // can change what's below the question (messages, streamed text), and on any
  // later size change inside the list (markdown, images, charts settling) or of
  // the viewport itself.
  useLayoutEffect(() => {
    updateTailSpace();
  }, [messages, streamingContent, updateTailSpace]);

  useEffect(() => {
    const container = ref.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => updateTailSpace());
    observer.observe(container);
    const content = container.querySelector('.chat-container__messages');
    if (content) observer.observe(content);
    return () => observer.disconnect();
  }, [messages, updateTailSpace]);

  // ── AI message: skip scroll after stream, scroll for history loads ────────
  useEffect(() => {
    const len = messages.length;
    const prev = prevLenEffect.current;
    prevLenEffect.current = len;

    if (len <= prev) return;
    const lastMsg = messages[len - 1];
    if (lastMsg?.role === 'user' || userScrolledUp.current) return;

    const justStreamed = streamingWasActive.current;
    streamingWasActive.current = false;
    if (justStreamed) return; // streamed response — leave the view where it is

    // Non-streamed AI addition (history load) — scroll to show it
    requestAnimationFrame(() => {
      const container = ref.current;
      if (!container) return;
      const allMsgs = container.querySelectorAll('.chat-msg');
      const lastMsgEl = allMsgs[allMsgs.length - 1] as HTMLElement | undefined;
      if (lastMsgEl) scrollToEl(container, lastMsgEl);
    });
  }, [messages, scrollToEl]);

  // ── Streaming: track only, no auto-follow ─────────────────────────────────
  // The response no longer drags the view down as it streams (or once it
  // finishes) — the view stays wherever the user left it (e.g. pinned to
  // their question above). streamingWasActive is still recorded so the
  // AI-message effect above can tell "just finished streaming" apart from
  // a genuine history load and skip scrolling for the former.
  useLayoutEffect(() => {
    if (!streamingContent) return;
    streamingWasActive.current = true;
  }, [streamingContent]);

  return ref;
}
